import moment from 'moment';

export const LUMPSUM_TABS = [
  { key: 'electricity', label: 'Electricity' },
  { key: 'water', label: 'Water' },
  { key: 'internet', label: 'Internet' },
  { key: 'cleaning', label: 'Cleaning' },
  { key: 'laundry', label: 'Laundry' },
  { key: 'service', label: 'Service' },
  { key: 'checkIn', label: 'Check In' },
  { key: 'checkOut', label: 'Check Out' },
];

// Same order as ExpensesFormDrawer tabs: Others, lumpsums, Toiletry.
export const BULK_EXPENSE_COLUMNS = [
  { key: 'others', label: 'Others' },
  ...LUMPSUM_TABS,
  { key: 'toiletry', label: 'Toiletry' },
];

export const BULK_EXPENSE_KEYS = BULK_EXPENSE_COLUMNS.map(col => col.key);

export const TOILETRY_TYPES = [
  { label: 'Body Wash', value: 'bodywash' },
  { label: 'Lotion', value: 'lotion' },
  { label: 'Shampoo', value: 'shampoo' },
  { label: 'Conditioner', value: 'conditioner' },
  { label: 'Shaver', value: 'shaver' },
  { label: 'Toilet Rolls', value: 'toiletrolls' },
  { label: 'Toothbrush', value: 'toothbrush' },
  { label: 'Toothpaste', value: 'toothpaste' },
];

export const LUMPSUM_KEYS = LUMPSUM_TABS.map(tab => tab.key);

export const ALL_SCALAR_KEYS = LUMPSUM_KEYS.concat([
  'indahWater',
  'fireInsurance',
  'strFees',
  'quitRent',
  'waterDispenser',
]);

export const normalizeId = id => {
  if (id == null || id === '') return '';
  if (typeof id === 'object' && id._id) return String(id._id);
  return String(id);
};

export const unitDisplayName = unit => {
  if (!unit) return '';
  return unit.name || unit.code || String(unit._id || '');
};

export const sumLineItems = rows =>
  (rows || []).reduce((sum, row) => sum + (Number(row && row.amount) || 0), 0);

export const emptyLine = (withDate = false) => {
  const line = {
    label: '',
    amount: 0,
    expensesAttachment: [],
    remarks: '',
  };
  if (withDate) {
    line.transactionDate = moment().format('YYYY-MM-DD');
  }
  return line;
};

const cloneAttachments = files => (Array.isArray(files) ? files.slice() : []);

const cloneLine = (row, withDate) => {
  const src = row || {};
  const line = {
    label: src.label || '',
    amount: Number(src.amount) || 0,
    remarks: src.remarks || '',
    expensesAttachment: cloneAttachments(src.expensesAttachment),
  };
  if (withDate) {
    line.transactionDate = src.transactionDate || moment().format('YYYY-MM-DD');
  }
  return line;
};

const amountsClose = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.005;

export const emptyUnitRow = (unitId, unitNo) => {
  const row = {
    unitId: String(unitId),
    unitNo: unitNo || String(unitId),
    others: 0,
    others_remarks: '',
    others_attachment: [],
    othersByHost: [emptyLine(true)],
    toiletry: [emptyLine(false)],
  };
  ALL_SCALAR_KEYS.forEach(key => {
    row[key] = 0;
    row[key + '_remarks'] = '';
    row[key + '_attachment'] = [];
  });
  return row;
};

const applyScalarFields = (target, src) => {
  ALL_SCALAR_KEYS.forEach(key => {
    target[key] = Number(src[key]) || 0;
    target[key + '_remarks'] = src[key + '_remarks'] || '';
    target[key + '_attachment'] = cloneAttachments(src[key + '_attachment']);
  });
  target.others = Number(src.others) || 0;
  target.others_remarks = src.others_remarks || '';
  target.others_attachment = cloneAttachments(src.others_attachment);
};

const mergeSavedRow = (saved, unitId, unitNo) => {
  const next = emptyUnitRow(unitId, unitNo);
  applyScalarFields(next, saved || {});
  const othersLines = Array.isArray(saved.othersByHost) ? saved.othersByHost : [];
  const toiletryLines = Array.isArray(saved.toiletry) ? saved.toiletry : [];
  next.othersByHost = othersLines.length ? othersLines.map(row => cloneLine(row, true)) : [emptyLine(true)];
  next.toiletry = toiletryLines.length ? toiletryLines.map(row => cloneLine(row, false)) : [emptyLine(false)];
  return next;
};

export const clonePackageLevelOntoUnit = (src, unitRow) => {
  const next = Object.assign({}, unitRow);
  applyScalarFields(next, src || {});
  const hostLines = Array.isArray(src.othersByHost) ? src.othersByHost : [];
  const hostSum = sumLineItems(hostLines);
  const othersScalar = Number(src.others) || 0;
  if (hostLines.length && (othersScalar === 0 || amountsClose(hostSum, othersScalar))) {
    next.othersByHost = hostLines.map(row => cloneLine(row, true));
  } else if (othersScalar > 0) {
    next.othersByHost = [
      {
        label: 'Others',
        amount: othersScalar,
        remarks: src.others_remarks || '',
        transactionDate: moment().format('YYYY-MM-DD'),
        expensesAttachment: cloneAttachments(src.others_attachment),
      },
    ];
  } else {
    next.othersByHost = [emptyLine(true)];
  }
  const toiletryLines = Array.isArray(src.toiletry) ? src.toiletry : [];
  next.toiletry = toiletryLines.length ? toiletryLines.map(row => cloneLine(row, false)) : [emptyLine(false)];
  return next;
};

export const rollupByUnitExpenses = expenses => {
  const src = expenses || {};
  const byUnit = Array.isArray(src.byUnit) ? src.byUnit : [];
  const rolled = Object.assign({}, src, { byUnit });
  ALL_SCALAR_KEYS.forEach(key => {
    rolled[key] = byUnit.reduce((sum, row) => sum + (Number(row && row[key]) || 0), 0);
    rolled[key + '_attachment'] = byUnit.reduce(
      (acc, row) => acc.concat((row && row[key + '_attachment']) || []),
      []
    );
    rolled[key + '_remarks'] = byUnit
      .map(row => (row && row[key + '_remarks']) || '')
      .filter(Boolean)
      .join('; ');
  });
  const othersByHost = byUnit.reduce((acc, row) => acc.concat((row && row.othersByHost) || []), []);
  const toiletry = byUnit.reduce((acc, row) => acc.concat((row && row.toiletry) || []), []);
  const filledOthers = othersByHost.filter(row => row && (row.label || Number(row.amount)));
  const filledToiletry = toiletry.filter(row => row && (row.label || Number(row.amount)));
  rolled.othersByHost = filledOthers;
  rolled.toiletry = filledToiletry;
  rolled.others = sumLineItems(filledOthers);
  rolled.others_attachment = [];
  rolled.others_remarks = src.others_remarks || '';
  rolled.othersByOwner = Array.isArray(src.othersByOwner) ? src.othersByOwner : [];
  return rolled;
};

export const byUnitMatchesPackageTotals = (byUnit, expenses) => {
  const rolled = rollupByUnitExpenses({ byUnit: byUnit || [] });
  const src = expenses || {};
  const keys = ALL_SCALAR_KEYS.concat(['others']);
  for (let i = 0; i < keys.length; i += 1) {
    if (!amountsClose(rolled[keys[i]], src[keys[i]])) return false;
  }
  if (!amountsClose(sumLineItems(rolled.othersByHost), sumLineItems(src.othersByHost))) return false;
  if (!amountsClose(sumLineItems(rolled.toiletry), sumLineItems(src.toiletry))) return false;
  return true;
};

const hasPackageLumpsum = src => {
  if (!src) return false;
  if (ALL_SCALAR_KEYS.some(key => Number(src[key]) > 0)) return true;
  if (Number(src.others) > 0) return true;
  if (sumLineItems(src.othersByHost) > 0) return true;
  if (sumLineItems(src.toiletry) > 0) return true;
  return false;
};

export const hydrateByUnit = ({ packageUnits, hostUnits, recordExpenses }) => {
  const src = recordExpenses || {};
  const unitIds = (packageUnits || []).map(normalizeId).filter(Boolean);
  const savedByUnit = Array.isArray(src.byUnit) ? src.byUnit : [];
  const useSaved = savedByUnit.length > 0 && byUnitMatchesPackageTotals(savedByUnit, src);
  const savedById = {};
  if (useSaved) {
    savedByUnit.forEach(row => {
      if (!row) return;
      savedById[normalizeId(row.unitId)] = row;
    });
  }

  const hostList = hostUnits || [];
  const copyLegacy = !useSaved && hasPackageLumpsum(src);

  return unitIds.map((id, idx) => {
    const hostUnit = hostList.find(unit => String(unit._id) === String(id));
    const unitNo = unitDisplayName(hostUnit) || id;
    if (useSaved && savedById[id]) {
      return mergeSavedRow(savedById[id], id, unitNo);
    }
    const empty = emptyUnitRow(id, unitNo);
    if (copyLegacy && idx === 0) {
      return clonePackageLevelOntoUnit(src, empty);
    }
    return empty;
  });
};

export const tabTotalsFromUnitRows = unitRows => {
  const rows = unitRows || [];
  const totals = {
    others: rows.reduce((sum, row) => sum + sumLineItems(row.othersByHost), 0),
    toiletry: rows.reduce((sum, row) => sum + sumLineItems(row.toiletry), 0),
  };
  LUMPSUM_KEYS.forEach(key => {
    totals[key] = rows.reduce((sum, row) => sum + (Number(row && row[key]) || 0), 0);
  });
  return totals;
};

export const cleanUnitRowsForSave = unitRows =>
  (unitRows || []).map(row => {
    const next = Object.assign({}, row);
    next.othersByHost = (row.othersByHost || [])
      .filter(line => line && (line.label || Number(line.amount)))
      .map(line => cloneLine(line, true));
    next.toiletry = (row.toiletry || [])
      .filter(line => line && (line.label || Number(line.amount)))
      .map(line => cloneLine(line, false));
    next.others = sumLineItems(next.othersByHost);
    return next;
  });
